'use client';
import posthog, { PostHogConfig } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { ReactNode, useEffect } from 'react';
import { dlog } from '../../dev';
import { capturePostHogEvent, POSTHOG_CONFIG } from './posthogConfig';
import { VelocityWindow } from 'src/window/velocityWindow';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import Env from 'src/environmentVariables/EnvironmentVariables';

const ENABLE_PERSISTENCE = false;

/**
 * Register static super properties that should be attached to every event.
 * Called at init and after posthog.reset() (which clears all registered properties).
 */
const registerStaticSuperProperties = () => {
	posthog.register({
		platform: 'web',
		env: Env.sdkEnv,
		user_agent: navigator.userAgent,
	});
};

const getOptionalPosthogParams = () => {
	const postHogParams: Partial<PostHogConfig> = {
		enable_recording_console_log: POSTHOG_CONFIG.consoleRecordingEnabled,
		session_recording: POSTHOG_CONFIG.sessionRecordingEnabled ? {} : undefined,
		capture_pageview: false, // Disabled — pageviews are captured manually in useRouterConfig on every SPA route change. Setting this to true would cause duplicate pageviews on initial load.
		capture_pageleave: true,
		disable_session_recording: POSTHOG_CONFIG.sessionRecordingEnabled
			? undefined
			: true,
	};

	return postHogParams;
};

const alreadyEnabledPosthog = { current: false };

export default function PHProvider({ children }: { children: ReactNode }) {
	const appEventEmitter = useVelocityStore((s) => s.appEventEmitter);
	const setVelocityStore = useVelocityStore((s) => s.set);

	useEffect(() => {
		if (alreadyEnabledPosthog.current) return;
		if (!appEventEmitter) return;

		dlog(`posthog`, `posthog_enabled`);

		alreadyEnabledPosthog.current = true;

		posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY ?? '', {
			api_host: 'https://t.velocity.exchange', // PostHog managed reverse proxy
			ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
			persistence: ENABLE_PERSISTENCE ? 'localStorage+cookie' : 'memory',
			loaded: (initializedPostHog) => {
				initializedPostHog.setPersonPropertiesForFlags({
					$current_url: window.location.href,
				});

				registerStaticSuperProperties();

				initializedPostHog.onFeatureFlags((flags) => {
					dlog(`feature_flags`, `onFeatureFlags`, flags);

					appEventEmitter.emit('featureFlagsLoaded', flags);
				});

				// Set up global unhandled error handler
				window.onerror = (message, source, lineno, colno, error) => {
					if (initializedPostHog.isFeatureEnabled('LOG_CONSOLE_ERRORS')) {
						const authority: string | undefined =
							// @ts-ignore — walletContext is injected at runtime
							window?.walletContext?.publicKey?.toString();
						capturePostHogEvent('system_unhandled_error', {
							error_message: message,
							source,
							lineno,
							colno,
							stack: error?.stack,
							url: window.location.href,
							timestamp: new Date().toISOString(),
							authority,
						});
					}
					return false;
				};

				// Set up unhandled promise rejection handler
				window.onunhandledrejection = (event) => {
					if (initializedPostHog.isFeatureEnabled('LOG_CONSOLE_ERRORS')) {
						const authority: string | undefined =
							// @ts-ignore — walletContext is injected at runtime
							window?.walletContext?.publicKey?.toString();
						capturePostHogEvent('system_unhandled_promise_rejection', {
							error_message: event.reason?.message || String(event.reason),
							stack: event.reason?.stack,
							url: window.location.href,
							timestamp: new Date().toISOString(),
							authority,
						});
					}
				};
			},
			autocapture: false,
			...getOptionalPosthogParams(),
		});
	}, [appEventEmitter]);

	useEffect(() => {
		const handler = (enabled: boolean) => {
			if (enabled) {
				dlog(`posthog`, `session_recording_enabled`);
				posthog.startSessionRecording();
			} else {
				dlog(`posthog`, `session_recording_disabled`);
				posthog.stopSessionRecording();
			}
		};

		VelocityWindow.devEventEmitter.on('set_session_recording', handler);

		return () => {
			VelocityWindow.devEventEmitter.off('set_session_recording', handler);
		};
	}, []);

	// Listen for changes to event capture setting
	useEffect(() => {
		const handleSetEventCapture = (enabled: boolean) => {
			dlog(`posthog`, `set_event_capture: ${enabled}`);
			setVelocityStore((s) => {
				s.postHog.enabled = enabled;
			});
		};

		VelocityWindow.devEventEmitter.on(
			'set_event_capture',
			handleSetEventCapture
		);

		// Cleanup function to remove listener
		return () => {
			VelocityWindow.devEventEmitter.off(
				'set_event_capture',
				handleSetEventCapture
			);
		};
	}, []);

	return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}

export const POSTHOG_UTILS = {
	registerStaticSuperProperties,
};
